import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';

export type CurrentContext = {
  profile: { id: string; displayName: string };
  household: { id: string; name: string; kind: 'personal' | 'family'; timezone: string };
  selfSubject: { id: string; slug: string; displayName: string };
  /**
   * The household-subject seeded at signup. Hidden from the UI while the space is personal
   * (A1), but its id is what an expense "for the family" belongs to (A2), so every screen
   * that can post an entry needs it.
   */
  householdSubject: { id: string; slug: string };
  /** false hides every household/family concept in the UI (decision A1). */
  isFamily: boolean;
};

/**
 * Resolves who the signed-in user is, creating their hidden personal space on first
 * login (spec v2 6 — the signup bootstrap flow).
 *
 * Bootstrapping is idempotent in practice: `app.create_household` refuses a second
 * household for the same user, and we simply re-read afterwards.
 */
export async function fetchCurrentContext(displayNameFallback: string): Promise<CurrentContext> {
  const existing = await readContext();
  if (existing) return existing;

  const { error } = await supabase
    .schema('app')
    .rpc('create_household', { p_display_name: displayNameFallback });

  // ALREADY_HAS_HOUSEHOLD means another tab won the race — re-read instead of failing.
  if (error && !error.message.includes('ALREADY_HAS_HOUSEHOLD')) {
    throw appErrorException(error);
  }

  const created = await readContext();
  if (!created) {
    throw appErrorException(new Error('Không tạo được không gian tài chính.'));
  }
  return created;
}

async function readContext(): Promise<CurrentContext | null> {
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) {
    throw appErrorException(new Error('unauthorized'));
  }

  const { data: households, error: householdError } = await supabase
    .from('households')
    .select('id, name, kind, timezone')
    .limit(1);
  if (householdError) throw appErrorException(householdError);

  const household = households?.[0];
  if (!household) return null;

  // Both subjects in one read: the person posting, and the household an expense can belong to.
  const { data: subjects, error: subjectError } = await supabase
    .from('financial_subjects')
    .select('id, slug, display_name, profile_id, subject_type');
  if (subjectError) throw appErrorException(subjectError);

  const selfSubject = subjects?.find(
    (subject) => subject.subject_type === 'person' && subject.profile_id === user.id,
  );
  const householdSubject = subjects?.find((subject) => subject.subject_type === 'household');
  if (!selfSubject || !householdSubject) return null;

  const { data: profile } = await supabase
    .from('profiles')
    .select('id, display_name')
    .eq('id', user.id)
    .maybeSingle();

  return {
    profile: {
      id: user.id,
      displayName: profile?.display_name ?? selfSubject.display_name,
    },
    household: {
      id: household.id,
      name: household.name,
      kind: household.kind === 'family' ? 'family' : 'personal',
      timezone: household.timezone,
    },
    selfSubject: {
      id: selfSubject.id,
      slug: selfSubject.slug,
      displayName: selfSubject.display_name,
    },
    householdSubject: { id: householdSubject.id, slug: householdSubject.slug },
    isFamily: household.kind === 'family',
  };
}

/**
 * Renames the signed-in person.
 *
 * Writes `profiles.display_name` only. `financial_subjects.display_name` is deliberately
 * left alone: that row is the accounting subject, its `slug` is the contract with
 * `knowledge/people/<slug>` (decision J3), and the baseline grants the client no UPDATE on
 * it ("mutated only through RPCs"). Renaming the accounting subject is a separate,
 * RPC-backed job that belongs with family management (step 1.8).
 */
export async function updateProfileDisplayName(displayName: string): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) {
    throw appErrorException(new Error('unauthorized'));
  }

  const { error } = await supabase
    .from('profiles')
    .update({ display_name: displayName })
    .eq('id', user.id);
  if (error) throw appErrorException(error);
}
